import { Task } from '../types';

export const cleanChecklist = (
  list: Task['checklist'] | Array<{ id?: string | number; text?: string; completed?: boolean }> | undefined
) => {
  if (!list || !Array.isArray(list)) return '[]';
  const cleaned = list.map(item => ({
    id: item.id ? String(item.id) : '',
    text: item.text || '',
    completed: !!item.completed
  }));
  cleaned.sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(cleaned);
};

export const cleanNotes = (notes: Task['notes'] | unknown) => {
  if (typeof notes === 'string') return notes;
  if (!notes || !Array.isArray(notes)) return '[]';
  const cleaned = notes.map((n) => {
    const item = typeof n === 'object' && n !== null ? (n as Record<string, unknown>) : {};
    return {
      id: item.id ? String(item.id) : '',
      text: typeof item.text === 'string' ? item.text : '',
      timestamp: Number(item.timestamp || 0),
      author: typeof item.author === 'string' ? item.author : '',
    };
  });
  cleaned.sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(cleaned);
};

export const cleanHistory = (
  history:
    | Task['history']
    | Array<{
        id?: string | number;
        timestamp?: number;
        user?: string;
        field?: string;
        oldValue?: unknown;
        newValue?: unknown;
        message?: string;
      }>
    | undefined
) => {
  if (!history || !Array.isArray(history)) return '[]';
  const cleaned = history.map(h => ({
    id: h.id ? String(h.id) : '',
    timestamp: Number(h.timestamp || 0),
    user: h.user || '',
    field: h.field || '',
    oldValue: h.oldValue !== undefined ? String(h.oldValue) : '',
    newValue: h.newValue !== undefined ? String(h.newValue) : '',
    message: h.message || ''
  }));
  cleaned.sort((a, b) => (a.id || String(a.timestamp)).localeCompare(b.id || String(b.timestamp)));
  return JSON.stringify(cleaned);
};

export const getCleanTaskData = (t: Task) => {
  return {
    id: String(t.id),
    globalId: (t.globalId && String(t.globalId).trim() !== '') ? String(t.globalId) : undefined,
    name: t.name || '',
    description: t.description || '',
    start: t.start || '',
    end: t.end || '',
    startTime: t.startTime || '',
    endTime: t.endTime || '',
    subtitle: t.subtitle || '',
    notes: cleanNotes(t.notes),
    type: t.type || 'task',
    completed: !!t.completed,
    status: t.status || 'todo',
    assignee: t.assignee || 'all',
    projectId: t.projectId || '',
    projectIds: JSON.stringify([...(t.projectIds || []).map(String)].sort()),
    contactId: t.contactId !== undefined && t.contactId !== null ? String(t.contactId) : '',
    personKey: t.personKey || '',
    completedAt: Number(t.completedAt || 0),
    unstacked: !!t.unstacked,
    parentId: t.parentId ? String(t.parentId) : '',
    priority: t.priority || 'none',
    dependencies: JSON.stringify([...(t.dependencies || []).map(String)].sort()),
    color: t.color || '',
    category: t.category || '',
    organization: t.organization || '',
    contactEmail: t.contactEmail || '',
    contactPhone: t.contactPhone || '',
    lastContactDate: t.lastContactDate || '',
    role: t.role || '',
    goals: t.goals || '',
    socialCapital: Number(t.socialCapital || 0),
    mood: t.mood || '',
    contactFunction: t.contactFunction || '',
    sortOrder: Number(t.sortOrder || 0),
    contactChecklist: cleanChecklist(t.contactChecklist),
    checklist: cleanChecklist(t.checklist),
    archived: !!t.archived,
    createdAt: Number(t.createdAt || 0),
    updatedAt: Number(t.updatedAt || 0),
    timerStartedAt: Number(t.timerStartedAt || 0),
    trackedTime: Number(t.trackedTime || 0),
    history: cleanHistory(t.history)
  };
};

export const areTaskListsEqual = (a: Task[] | undefined, b: Task[] | undefined): boolean => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) {
    return false;
  }
  if (a.length === 0) return true;

  // Fast path: all task references are strictly identical
  let allSameRef = true;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      allSameRef = false;
      break;
    }
  }
  if (allSameRef) return true;

  const mapA = new Map<string, Task>();
  for (const t of a) {
    mapA.set(String(t.id), t);
  }

  const cleanJsonA = new Map<string, string>();

  for (const tB of b) {
    const idStr = String(tB.id);
    const tA = mapA.get(idStr);
    if (!tA) {
      return false;
    }
    // Fast path: individual task reference equality
    if (tA === tB) continue;

    const cleanB = JSON.stringify(getCleanTaskData(tB));
    let cleanA = cleanJsonA.get(idStr);
    if (!cleanA) {
      cleanA = JSON.stringify(getCleanTaskData(tA));
      cleanJsonA.set(idStr, cleanA);
    }
    if (cleanA !== cleanB) {
      return false;
    }
  }
  return true;
};
